import { Logger } from '@gitbook/util-debug';
import { assertNever } from '@gitbook/util-ts';

import type { Operation } from '../models';
import { pick } from '../utils';

const logger = Logger('slate:operation:invert');

/**
 * Invert an operation.
 */
export function invertOperation(op: Operation): Operation {
    const { type } = op;

    /*
     * Insert node.
     */
    if (type === 'insert_node') {
        return op.set('type', 'remove_node');
    }

    /*
     * Set fragment.
     */
    if (type === 'set_fragment') {
        return op.set('type', 'remove_fragment').set('fragment', op.get('node').key);
    }

    /*
     * Insert fragment.
     */
    if (type === 'insert_fragment') {
        return op.set('type', 'remove_fragment').set('fragment', op.get('node').key);
    }

    /*
     * Remove node.
     */
    if (type === 'remove_node') {
        return op.set('type', 'insert_node');
    }

    /*
     * Remove fragment.
     */
    if (type === 'remove_fragment') {
        return op.set('type', 'insert_fragment').delete('fragment');
    }

    /*
     * Move node.
     */
    if (type === 'move_node') {
        const { newPath, path } = op;
        let inversePath = newPath;
        let inverseNewPath = path;

        const pathLast = path.length - 1;
        const newPathLast = newPath.length - 1;

        // If the node's old position was a left sibling of an ancestor of
        // its new position, we need to adjust part of the path by -1.
        if (
            path.length < inversePath.length &&
            path.slice(0, pathLast).every((e, i) => e === inversePath[i]) &&
            path[pathLast] < inversePath[pathLast]
        ) {
            inversePath = inversePath
                .slice(0, pathLast)
                .concat([inversePath[pathLast] - 1])
                .concat(inversePath.slice(pathLast + 1, inversePath.length));
        }

        // If the node's new position is an ancestor of the old position,
        // or a left sibling of an ancestor of its old position, we need
        // to adjust part of the path by 1.
        if (
            newPath.length < inverseNewPath.length &&
            newPath.slice(0, newPathLast).every((e, i) => e === inverseNewPath[i]) &&
            newPath[newPathLast] <= inverseNewPath[newPathLast]
        ) {
            inverseNewPath = inverseNewPath
                .slice(0, newPathLast)
                .concat([inverseNewPath[newPathLast] + 1])
                .concat(inverseNewPath.slice(newPathLast + 1, inverseNewPath.length));
        }

        return op.set('path', inversePath).set('newPath', inverseNewPath);
    }

    /*
     * Merge node.
     */
    if (type === 'merge_node') {
        const { path } = op;
        const { length } = path;
        const last = length - 1;
        const inversePath = path.slice(0, last).concat([path[last] - 1]);
        const inverse = op.set('type', 'split_node').set('path', inversePath);
        return inverse;
    }

    /*
     * Split node.
     */
    if (type === 'split_node') {
        const { path } = op;
        const { length } = path;
        const last = length - 1;
        const inversePath = path.slice(0, last).concat([path[last] + 1]);
        const inverse = op.set('type', 'merge_node').set('path', inversePath);
        return inverse;
    }

    /*
     * Set node.
     */
    if (type === 'set_node') {
        const { properties, node } = op;
        const inverseNode = node.merge(properties);
        const inverseProperties = pick(node, Object.keys(properties));
        const inverse = op.set('node', inverseNode).set('properties', inverseProperties);
        return inverse;
    }

    /*
     * Insert text.
     */
    if (type === 'insert_text') {
        return op.set('type', 'remove_text');
    }

    /*
     * Remove text.
     */
    if (type === 'remove_text') {
        return op.set('type', 'insert_text');
    }

    /*
     * Insert data.
     */

    if (type === 'set_data') {
        if (op.get('previousData') === undefined) {
            return op.merge({
                type: 'remove_data',
            });
        }
        return op.merge({
            type: 'set_data',
            data: op.get('previousData'),
            previousData: op.get('data'),
        });
    }

    /*
     * Remove data.
     */

    if (type === 'remove_data') {
        return op.set('type', 'set_data');
    }

    /*
     * Insert data in array.
     */
    if (type === 'insert_array_data') {
        return op.set('type', 'remove_array_data').delete('dataIndex');
    }

    /*
     * Remove data from array.
     */
    if (type === 'remove_array_data') {
        return op.set('type', 'insert_array_data');
    }

    /*
     * Add mark.
     */
    if (type === 'add_mark') {
        return op.set('type', 'remove_mark');
    }

    /*
     * Remove mark.
     */
    if (type === 'remove_mark') {
        return op.set('type', 'add_mark');
    }

    /*
     * Set selection.
     */
    if (type === 'set_selection') {
        const { properties, selection } = op;

        // operation.properties contains a dictionary of all keys that changed. Set them back
        // to their original values.
        const inverseSelection = selection.merge(properties);
        const inverseProps = pick(selection, Object.keys(properties));

        const inverse = op.set('selection', inverseSelection).set('properties', inverseProps);
        return inverse;
    }

    /*
     * Set document.
     */
    if (type === 'set_document') {
        logger.error('Trying to invert a `set_document` operation which is forbidden.');
        return op;
    }

    assertNever(type);
}
